import { IRole } from "../../domain/role.interface";

/**
 * SIMS-8762 AC1 (PRD FR-16) — which roles grant an action.
 *
 * Deleting an action that a role still grants would revoke live access as a side effect of
 * tidying the catalogue, so the delete is refused and the refusal has to NAME the roles: the
 * recovery is to remove the action from them, and a bare count leaves the administrator hunting.
 *
 * `IRole` carries the grant under two names — `actions` on the read path and `actionIds` on some
 * write shapes — and either may be absent. Both are read here rather than at the call site, so a
 * role whose grants arrive under the other key cannot silently look ungranting and let a live
 * permission be deleted.
 *
 * DELIBERATELY INDEPENDENT OF GROUP STATUS (case C17632). An action in an INACTIVE group is still
 * granted by whatever roles reference it; the group being hidden from the permission screen does
 * not make the grant go away. Filtering by anything but the id would let compliance item C-1's
 * hidden grants become deletable, silently revoking access nobody could see.
 */
export const grantIdsOf = (role: IRole): string[] =>
	role.actions ?? role.actionIds ?? [];

/** The roles granting `actionId`, in the order the roles list returned them. */
export const rolesGranting = (
	roles: IRole[] | undefined,
	actionId: string,
): IRole[] =>
	(roles ?? []).filter((role) => grantIdsOf(role).includes(actionId));

/**
 * Role names for the refusal message.
 *
 * Names only — never an id, and never a user. TestRail C17630 draws the line this function sits
 * on: roles are configuration and may be named; people may not.
 */
export const grantingRoleNames = (
	roles: IRole[] | undefined,
	actionId: string,
): string[] =>
	rolesGranting(roles, actionId)
		.map((role) => role.name)
		.filter((name): name is string => Boolean(name));
